import * as React from 'react'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Badge } from './badge'
import { Button } from './button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

export type ComboboxOption = { value: string; label: string }

/**
 * A plain (non ra-core-form-bound) creatable single-value combobox: a
 * dropdown of known options that also accepts an arbitrary typed value not
 * in the list. `@/components/admin/autocomplete-input.tsx` is the closest
 * existing pattern (same `Command`+`Popover` primitives) but is wired to
 * ra-core's `useInput`/react-hook-form — not usable from a plain `useState`
 * dialog/drawer, which is what every caller of this component is.
 */
export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  allowCustomValue = false,
  emptyText = 'No matching item found.',
  className,
  id,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
}: {
  options: ComboboxOption[]
  value: string | null | undefined
  onChange: (value: string) => void
  placeholder?: string
  allowCustomValue?: boolean
  emptyText?: string
  className?: string
  id?: string
  /** Controls the popover's own search box from the outside — for callers that drive it off a server-side (e.g. debounced) query instead of client-filtering `options`. Uncontrolled (internal state) when omitted, as before. */
  searchValue?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
}) {
  const [open, setOpen] = React.useState(false)
  const [internalSearch, setInternalSearch] = React.useState('')
  const isSearchControlled = searchValue !== undefined && onSearchChange !== undefined
  const search = isSearchControlled ? searchValue : internalSearch
  const setSearch = isSearchControlled ? onSearchChange : setInternalSearch
  const selected = options.find((option) => option.value === value)
  const canCreate = allowCustomValue && search.trim() !== '' && !options.some((option) => option.value === search.trim())

  // Typing a custom value only ever committed via the "Use …" item's own
  // onSelect — closing the popover any other way (Escape, clicking outside)
  // silently discarded whatever was typed, reported as the free-text address
  // entry "not working". Committing on every close (not just an explicit
  // select) makes a typed value behave like a normal text input's own value.
  const handleOpenChange = (next: boolean) => {
    if (!next && canCreate) {
      onChange(search.trim())
      setSearch('')
    }
    setOpen(next)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between font-normal', className)}
        >
          <span className="min-w-0 flex-1 truncate text-left">
            {selected ? selected.label : value || <span className="text-muted-foreground">{placeholder}</span>}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full max-w-(--radix-popover-trigger-width) p-0">
        <Command>
          <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={setSearch} />
          {/* Overrides the shared `Command`'s fixed 300px cap with the popover's actual available viewport height, same `--radix-*-available-height` technique `SelectContent` already uses — a long option list (e.g. hundreds of slots) otherwise only ever shows ~8 rows before scrolling, reading as "capped". */}
          <CommandList className="max-h-(--radix-popover-content-available-height)">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  keywords={[option.label]}
                  onSelect={() => {
                    onChange(option.value)
                    setSearch('')
                    setOpen(false)
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === option.value ? 'opacity-100' : 'opacity-0')} />
                  {option.label}
                </CommandItem>
              ))}
              {canCreate && (
                <CommandItem
                  value={`?${search}?`}
                  onSelect={() => {
                    onChange(search.trim())
                    setSearch('')
                    setOpen(false)
                  }}
                >
                  Use "{search.trim()}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/** The multi-value counterpart of {@link Combobox} — same creatable behavior, an array of selected values shown as removable badges. */
export function MultiCombobox({
  options,
  value,
  onChange,
  placeholder = 'Select options',
  allowCustomValue = false,
  className,
  id,
}: {
  options: ComboboxOption[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  allowCustomValue?: boolean
  className?: string
  id?: string
}) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const canCreate = allowCustomValue && search.trim() !== '' && !value.includes(search.trim())

  const toggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue))
    } else {
      onChange([...value, optionValue])
    }
  }

  const labelFor = (optionValue: string) => options.find((option) => option.value === optionValue)?.label ?? optionValue

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((optionValue) => (
            <Badge key={optionValue} variant="secondary" className="gap-1">
              {labelFor(optionValue)}
              <button type="button" onClick={() => toggle(optionValue)} aria-label={`Remove ${labelFor(optionValue)}`}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            <span className="text-muted-foreground">{placeholder}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full max-w-(--radix-popover-trigger-width) p-0">
          <Command>
            <CommandInput placeholder="Search..." value={search} onValueChange={setSearch} />
            {/* See `Combobox`'s own `CommandList` above — same fixed-300px-cap fix. */}
            <CommandList className="max-h-(--radix-popover-content-available-height)">
              <CommandEmpty>No matching item found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    keywords={[option.label]}
                    onSelect={() => toggle(option.value)}
                  >
                    <Check className={cn('mr-2 h-4 w-4', value.includes(option.value) ? 'opacity-100' : 'opacity-0')} />
                    {option.label}
                  </CommandItem>
                ))}
                {canCreate && (
                  <CommandItem
                    value={`?${search}?`}
                    onSelect={() => {
                      onChange([...value, search.trim()])
                      setSearch('')
                    }}
                  >
                    Add "{search.trim()}"
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
